import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  Autocomplete,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  FormControlLabel,
  Grid,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DownloadIcon from "@mui/icons-material/Download";

import {
  getAllGenerateHistory,
  getGenerateHistoryBySeller,
} from "src/api/generateHistory";

import font from "src/styles/font";
import device from "src/styles/device";
import { Black, Grey } from "src/styles/color";
import { checkNotEmpty } from "src/common";
import { useAuth } from "src/hooks/useAuth";
import { UseUpload } from "src/context/UploadProductContext";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import Button from "src/components/core/Button";
import Checkbox from "src/components/core/Checkbox";

const WrapperHeaderFixed = styled.div`
  width: 100%;
  /* max-width: 1240px; */
  position: sticky;
  top: 0;
  background-color: #ffffff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const HeaderFixed = styled.div`
  width: 100%;
  min-height: 80px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media ${device.tabletL} {
    padding: 16px 20px;
  }
`;

const HeaderItem = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  /* align-self: ${({ alignSelf }) => (alignSelf ? alignSelf : "unset")}; */
  gap: 16px;
`;

const HeaderSubItem = styled.div`
  width: max-content;
  display: flex;
  align-items: ${({ alignItem }) => (alignItem ? alignItem : "center")};
  flex-direction: ${({ column }) => (column ? "column" : "row")};
  column-gap: 16px;
  row-gap: 0;

  @media ${device.mobileL} {
    flex-direction: column;
  }
`;

const WrapperContent = styled.div`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 24px;
`;

const SpanTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: 500;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSubTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: 300;

  span {
    font-size: ${font.size.md};
    font-weight: 300;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const GenerateHistory = ({ open }) => {
  const { user, userTokenBalance } = useAuth();
  const navigate = useNavigate();
  const { masterdata } = UseUpload();
  const { seller_data } = masterdata;

  const [historyData, setHistoryData] = useState([]);
  const pageSize = 15;
  const [dataSearch, setDataSearch] = useState({
    page: 1,
    page_size: pageSize,
    sellerIds: [],
  });
  const [totalPage, setTotalPage] = useState(1);
  const [isAdmin, setIsAdmin] = useState(true);
  const [isSeller, setIsSeller] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAllHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.page, dataSearch.sellerIds, isAdmin, isSeller]);

  const fetchAllHistory = async () => {
    try {
      setIsLoading(true);
      let responseData = null;
      if (user?.role_id === 3) {
        const response = await getGenerateHistoryBySeller({
          ...dataSearch,
          page: dataSearch.page,
        });
        responseData = response;
      } else {
        const response = await getAllGenerateHistory({
          ...dataSearch,
          page: dataSearch.page,
          user: checkNotEmpty(dataSearch.sellerIds)
            ? dataSearch.sellerIds.join(",")
            : null,
          isAdmin: isAdmin,
          isSeller: isSeller,
        });
        responseData = response;
      }
      setHistoryData(responseData?.histories);
      setTotalPage(Math.ceil(responseData?.pagination?.total / pageSize));
    } catch (error) {
      console.error("fetch generate history error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const seller_option = seller_data.map((sel) => {
    return {
      ...sel,
      userRoles: [1],
      value: sel.user._id,
      label: sel.seller.company_name,
    };
  });

  const filteredData = checkNotEmpty(dataSearch.sellerIds)
    ? seller_option.filter((item) => dataSearch.sellerIds.includes(item.value))
    : [];

  const handleChange = (event, values) => {
    const selectedSellers = values.map((val) => val.value);
    setDataSearch((prev) => ({
      ...prev,
      page: 1, // reset page to 1 when changing seller filter
      sellerIds: selectedSellers.length > 0 ? selectedSellers : [],
    }));
  };

  const handleCheckBox = (type, value) => {
    if (type === "admin") {
      setIsAdmin(value);
    } else if (type === "seller") {
      setIsSeller(value);
    }

    setDataSearch((prev) => ({
      ...prev,
      page: 1, // reset page to 1 when changing checkbox filter
    }));
  };

  return (
    <>
      <Main open={open}>
        <WrapperHeaderFixed>
          <HeaderFixed>
            <HeaderItem>
              {user?.role_id === 1 && (
                <>
                  <HeaderSubItem alignItem="flex-start">
                    <FormControlLabel
                      sx={{
                        marginInline: 0,
                      }}
                      label="Gen by Admin"
                      control={
                        <Checkbox
                          checked={isAdmin}
                          onChange={(e) =>
                            handleCheckBox("admin", e.target.checked)
                          }
                        />
                      }
                    />
                    <FormControlLabel
                      sx={{
                        marginInline: 0,
                      }}
                      label="Gen by Seller"
                      control={
                        <Checkbox
                          checked={isSeller}
                          onChange={(e) =>
                            handleCheckBox("seller", e.target.checked)
                          }
                        />
                      }
                    />
                  </HeaderSubItem>

                  {isSeller && ( // show seller filter only when isSeller is true
                    <Autocomplete
                      multiple
                      id="checkboxes-tags-demo"
                      limitTags={1}
                      options={seller_option}
                      value={filteredData}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.label}
                      onChange={handleChange}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.label
                      }
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Checkbox
                              icon={
                                <CheckBoxOutlineBlankIcon fontSize="small" />
                              }
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.label}
                          </li>
                        );
                      }}
                      sx={{
                        minWidth: 250,
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seller"
                          placeholder="company"
                        />
                      )}
                    />
                  )}
                </>
              )}
            </HeaderItem>

            {user?.role_id === 3 && (
              <HeaderItem>
                <HeaderSubItem column alignItem="flex-end">
                  <SpanSubTitle>ยอดคงเหลือ Token ปัจจุบัน</SpanSubTitle>
                  <SpanTitle>
                    {userTokenBalance?.current_balance || 0} Tokens
                  </SpanTitle>
                </HeaderSubItem>

                <Button
                  variant="contained"
                  onClick={() => navigate("/token-topup")}
                >
                  เติม Token
                </Button>
              </HeaderItem>
            )}
          </HeaderFixed>
        </WrapperHeaderFixed>

        <WrapperContent>
          <Grid
            container
            justifyContent="space-around"
            columnSpacing={4}
            rowSpacing={4}
          >
            {historyData.length > 0 ? (
              historyData.map((item) => (
                <Grid item xs="auto" key={item._id}>
                  <Card
                    sx={{
                      mx: "auto",
                      width: 300,
                      borderRadius: 4,
                    }}
                  >
                    <CardMedia
                      sx={{ height: 300 }}
                      image={item?.image_url}
                      title="green iguana"
                    />

                    <CardContent>
                      <Typography
                        gutterBottom
                        variant="subtitle1"
                        component="div"
                      >
                        Product Code: {item?.item_code || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={Grey}
                        sx={{ mb: 1, fontSize: font.size.sm }}
                      >
                        {dayjs(item?.createdAt).format("DD/MM/YYYY HH:mm")}
                      </Typography>
                      <Typography variant="body2">
                        ใช้ไป: {item?.token_used} เครดิต
                      </Typography>
                    </CardContent>

                    <CardActions
                      sx={{ p: 2, pt: 0, justifyContent: "flex-end" }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          window.open(`${item?.image_url}`, "_blank");
                        }}
                      >
                        Download
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12} sx={{ minHeight: 200 }}>
                <Typography variant="h6" align="center">
                  ไม่พบประวัติการสร้างภาพ
                </Typography>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Pagination
                count={totalPage}
                page={dataSearch.page}
                onChange={(_, value) =>
                  setDataSearch({ ...dataSearch, page: value })
                }
              />
            </Grid>
          </Grid>
        </WrapperContent>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default GenerateHistory;
